/**
 * v2.56 — Price Upload API client.
 *
 * CSRF/multipart pattern: read the `pacebi_csrf` cookie and set X-CSRF-Token
 * on every state-changing POST. For multipart (FormData) bodies, MUST NOT set
 * Content-Type — let the browser inject the boundary automatically.
 */
import type { PriceValidateResponse, PriceJob, PriceJobDetail } from './types';

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}

function csrfMultipartHeaders(): Record<string, string> {
  const csrf = readCookie('pacebi_csrf');
  const h: Record<string, string> = {};
  if (csrf) h['X-CSRF-Token'] = csrf;
  return h;
}

/**
 * Validate a price CSV without committing any writes.
 * POST multipart to /api/price-uploads/validate.
 */
export async function validatePriceFile(
  file: File,
  stores: number[],
): Promise<PriceValidateResponse> {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('stores', stores.join(','));

  const res = await fetch('/api/price-uploads/validate', {
    method: 'POST',
    credentials: 'same-origin',
    headers: csrfMultipartHeaders(),
    body: fd,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }
  return (await res.json()) as PriceValidateResponse;
}

/**
 * Dispatch a price upload job.
 * POST multipart to /api/price-uploads.
 * 404 → throws Error('writes_disabled').
 * 422 → throws Error with the server's errors array joined.
 */
export async function dispatchPriceUpload(
  file: File,
  stores: number[],
  createMissing: boolean,
  skipErrors: boolean,
): Promise<{ job_id: string }> {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('stores', stores.join(','));
  fd.append('create_missing', String(createMissing));
  fd.append('skip_errors', String(skipErrors));

  const res = await fetch('/api/price-uploads', {
    method: 'POST',
    credentials: 'same-origin',
    headers: csrfMultipartHeaders(),
    body: fd,
  });

  if (res.status === 404) throw new Error('writes_disabled');

  if (res.status === 422) {
    const payload = await res.json().catch(() => ({ errors: [] }));
    const msgs = (payload.errors ?? []).map(
      (e: { line_num: number; field: string; message: string }) =>
        `line ${e.line_num} ${e.field}: ${e.message}`,
    );
    throw new Error((msgs as string[]).join('; ') || `HTTP 422`);
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }

  return (await res.json()) as { job_id: string };
}

/**
 * List all price upload jobs for this session.
 * GET /api/price-uploads.
 */
export async function listPriceJobs(): Promise<PriceJob[]> {
  const res = await fetch('/api/price-uploads', {
    credentials: 'same-origin',
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }
  return (await res.json()) as PriceJob[];
}

/**
 * Get detail for a single price upload job.
 * GET /api/price-uploads/:id.
 */
export async function getPriceJob(id: string): Promise<PriceJobDetail> {
  const res = await fetch(`/api/price-uploads/${id}`, {
    credentials: 'same-origin',
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }
  return (await res.json()) as PriceJobDetail;
}

/**
 * Returns the URL for downloading the audit CSV of a job.
 * Pure URL helper — does not call fetch.
 */
export function priceAuditCsvUrl(id: string): string {
  return `/api/price-uploads/${id}/audit.csv`;
}

/**
 * Fetch the list of real stores (SID > 0) from /api/stores.
 * GET /api/stores, credentials: same-origin.
 */
export async function getStores(): Promise<import('./types').Store[]> {
  const res = await fetch('/api/stores', {
    credentials: 'same-origin',
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }
  return (await res.json()) as import('./types').Store[];
}
